import { isWatermarkRidsPresent, Mode } from '@sharefiledev/fileviewer-pilet-sdk';
import { useAppContext } from '../../../AppContext';
import { usePluginContext } from '../../../PermissionsContext/usePluginContext';
import {
	ConditionalLinkOptionsModalContentType,
	DEFAULT_EXPIRATION_DAYS,
} from '../../constants';
import { getFilteredExpirationOptions } from '../../helpers/ExpiryOptions';
import { useLinkContext } from '../customHooks/useLinkBasedShare';
import {
	ConditionalLinkOptionModalRenderProps,
	ConditionalLinkOptionsProps,
} from '../utils';
import { findNextBestCombination } from '../utils/findNextBestCombination';
import { findNextBestCombinationForOtherPlugin } from '../utils/findNextBestCombinationForOtherPlugin';
import {
	getConditionalLinkOptionsForFirstTime,
	getConditionalLinkOptionsForFirstTimeByType,
	getFilteredPresets,
	getFilteredPresetsByType,
	getPermissionKeyFromContext,
	getPermissionKeyFromContextByType,
	getPresetInfoByPresetRID,
	getPresetInfoByTypeByPresetRID,
	isConfigurationByResourceType,
	isStoragePlugin,
} from '../utils/linkShare-utils';

export const BuildConditionalLinkOptionsProps = ({
	props,
}: {
	props: ConditionalLinkOptionModalRenderProps;
}): ConditionalLinkOptionsProps => {
	const linkBasedContext = useLinkContext();
	const pluginContext = usePluginContext();
	const { account } = useAppContext();
	const { linkOptionsContentType, conditionalLinks } = linkBasedContext;

	const isConfigurationByResourceTypePresent = isConfigurationByResourceType(
		pluginContext.availablePermissionSetsByType,
		pluginContext.presetsByResourceType
	);

	const filteredExpirationOptions = getFilteredExpirationOptions(
		props.expirationOptions,
		props.accountLevelLinkSettings
	);
	const filterPresetsByType = getFilteredPresetsByType(
		pluginContext.sharablePresetsByType,
		props.availablePermissions
	);
	const filteredPresets = getFilteredPresets(
		pluginContext.sharablePresets,
		props.availablePermissions
	);

	const commonProps = {
		availablePermissions: props.availablePermissions,
		availableConditions: props.availableConditions,
		availableExpiryOptions: filteredExpirationOptions,
		selectedExpiry: filteredExpirationOptions.includes(linkBasedContext.expiryDate)
			? linkBasedContext.expiryDate
			: filteredExpirationOptions[0],
		latestVersionFlag: linkBasedContext.latestVersionFlag,
		watermarkEnabled: false, // caching to be added later
		notifyMeFlag: linkBasedContext.notifyMeFlag,
		selectedLink: props.selectedLink,
		sharablePresets: filteredPresets,
		resourceList: pluginContext.selectedResources.map(r => r.rid),
		sharablePresetsByType: filterPresetsByType,
		presetsByResourceType: pluginContext.presetsByResourceType,
		availablePermissionSetsByType: pluginContext.availablePermissionSetsByType,
	};

	if (
		linkOptionsContentType ===
		ConditionalLinkOptionsModalContentType.EditLinkOptionBeforeFirstLinkCreation
	) {
		const newLinkSettings = isConfigurationByResourceTypePresent
			? getConditionalLinkOptionsForFirstTimeByType(
					props.accountLevelLinkSettings,
					pluginContext.selectedResources,
					pluginContext.sharablePresetsByType,
					account.Preferences?.EnableOfficeOnlineEditing ?? false,
					linkBasedContext.consumerPluginName
			  )
			: getConditionalLinkOptionsForFirstTime(
					props.accountLevelLinkSettings,
					pluginContext.selectedResources,
					pluginContext.sharablePresets,
					account.Preferences?.EnableOfficeOnlineEditing ?? false,
					linkBasedContext.consumerPluginName
			  );

		return {
			...commonProps,
			selectedPermission: isConfigurationByResourceTypePresent
				? getPresetInfoByTypeByPresetRID(
						newLinkSettings.presetRID,
						pluginContext.sharablePresetsByType
				  )?.name ?? ''
				: getPresetInfoByPresetRID(
						newLinkSettings.presetRID,
						pluginContext.sharablePresets
				  )?.name ?? '',
			selectedConditions: newLinkSettings.condition ?? props.availableConditions[0],
			selectedExpiry:
				newLinkSettings.expirationDays !== null &&
				filteredExpirationOptions.includes(newLinkSettings.expirationDays)
					? newLinkSettings.expirationDays
					: filteredExpirationOptions[0],
			latestVersionFlag: newLinkSettings.useLatestVersion ?? true,
			notifyMeFlag: newLinkSettings.notify ?? true,
			watermarkMode: Mode.untickedCheckboxWithoutTransform,
		};
	}

	// Case 2: Edit existing link
	if (
		linkOptionsContentType ===
		ConditionalLinkOptionsModalContentType.EditExistingLinkOption
	) {
		const isWaterMarkedLink = isWatermarkRidsPresent(props.selectedLink.resourceList);

		return {
			...commonProps,
			selectedPermission: isConfigurationByResourceTypePresent
				? getPermissionKeyFromContextByType(
						props.selectedLink.conditionalLinkOptions?.presetRID,
						isWaterMarkedLink
							? linkBasedContext.sharablePresetsByTypeForWatermarking
							: pluginContext.sharablePresetsByType
				  )
				: getPermissionKeyFromContext(
						props.selectedLink.conditionalLinkOptions?.presetRID,
						pluginContext.sharablePresets
				  ),
			selectedConditions:
				props.selectedLink?.conditionalLinkOptions?.condition ??
				props.availableConditions[0],
			selectedExpiry:
				props.selectedLink.conditionalLinkOptions?.expirationDays ??
				DEFAULT_EXPIRATION_DAYS,
			latestVersionFlag:
				props.selectedLink.conditionalLinkOptions?.useLatestVersion ?? true,
			watermarkEnabled: isWaterMarkedLink,
			resourceList: props.selectedLink.resourceList ?? [],
			notifyMeFlag: props.selectedLink.conditionalLinkOptions?.notify ?? true,
			watermarkMode: isWaterMarkedLink
				? Mode.tickedCheckboxWithoutTransform
				: Mode.untickedCheckboxWithoutTransform,
		};
	}

	const nextBestCombination = !isStoragePlugin(linkBasedContext.consumerPluginName)
		? findNextBestCombinationForOtherPlugin(
				props.availableConditions,
				props.availablePermissions,
				conditionalLinks,
				pluginContext.sharablePresets
		  )
		: findNextBestCombination(
				pluginContext.selectedResources,
				linkBasedContext.waterMarkFlag,
				props.availableConditions,
				props.availablePermissions,
				conditionalLinks,
				pluginContext.sharablePresets,
				account
		  );

	return {
		...commonProps,
		selectedPermission: nextBestCombination.permission,
		selectedConditions: nextBestCombination.condition,
		watermarkMode: Mode.untickedCheckboxWithoutTransform,
	};
};
